import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, DocumentDuplicateIcon, PencilSquareIcon, Square2StackIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import DateField from './DateField';
import { SelectField, deletedRefProps } from './fields';
import EditHistoryLink from './EditHistoryLink';
import FscTariffLinesDrawer from './FscTariffLinesDrawer';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import fscTariffsService, { FscTariffFormOptions, TariffPreviewResponse } from '../../services/fsctariffs.service';

/**
 * Bespoke create/edit drawer for a Fuel Surcharge Tariff (fueldata.fsc_tariff).
 * Faithful to V1 editfsc_tariff:
 *   - once the tariff has lines, the identity + rate-setup fields lock, but the
 *     effective/expiration dates and comments stay editable; dates additionally
 *     lock on a non-current revision (the Deleted flag is always free)
 *   - Fuel Index (GULF COAST/NATIONAL) + Rate Effective only apply to FUEL INDEX
 *   - "New Revision" clones the current tariff into the next revision (+ copies lines)
 *   - "Dupe" clones it into a BRAND-NEW tariff number at revision 1 (+ copies
 *     lines), leaving the source untouched — the way to stand up the same rate
 *     table for another bill-to without superseding this one
 *   - bill-to / customer are preloaded typeaheads; a read-only rate schedule shows
 *     the lines around the current index/date; tariff lines have their own sub-drawer
 */
const TABLE = 'fsc-tariff';

const TARIFF_TYPES = ['FUEL INDEX', 'WEEKLY/MONTHLY'];
const FUEL_INDEX_SRC = ['NATIONAL', 'GULF COAST', 'MIDWEST', 'EAST COAST', 'ROCKY MOUNTAIN', 'WEST COAST', 'NEW ENGLAND', 'CENTRAL ATLANTIC', 'LOWER ATLANTIC', 'WEST COAST LESS CALIFORNIA'];
// FLAT (a flat $ per move) is a FastCMS addition to V1's three.
const RATE_TYPES = ['STANDARD', 'PER MILE', 'PER GALLON', 'FLAT'];
const OTR_LOCAL = ['ALL', 'LOCAL', 'OTR', 'D/T/D'];
const RATE_EFFECTIVE = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'MONDAY AFTER'];

const NEW_DEFAULTS: TablemaintRow = {
  tarifftype: 'FUEL INDEX', fuelindex: 'NATIONAL', ratetype: 'STANDARD',
  otrlocal: 'ALL', rateeffective: 'MONDAY', billto: 0,
  billtoname: '', mas90code: '', name: '', effectivedate: '', expirationdate: '', comments: '',
};

const s = (v: any): string => (v == null ? '' : String(v));
const opts = (arr: string[]) => arr.map((v) => ({ value: v, label: v }));
const toInputDate = (v: any): string => {
  const sv = s(v);
  return !sv || sv.startsWith('0000') ? '' : sv.slice(0, 10);
};
const toStoreDate = (v: string): string => (v ? v : '0000-00-00');
const fmtMDY = (v: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s(v));
  return !m || m[1] === '0000' ? s(v) : `${m[2]}/${m[3]}/${m[1]}`;
};
/** Address (+ Sage tag) line shown under each bill-to option. The recno rides
 *  the option's labelSuffix (muted "#id" by the name, kept in the closed input
 *  by TypeAhead's fixed-option display) — identically named records (e.g. five
 *  "Cardinal Health" bill-tos) stay distinguishable everywhere. */
const addrDesc = (address: string, city: string, state: string, sage?: string): string => {
  const place = [titleCase(address), titleCase(city), state].filter(Boolean).join(', ');
  return [place, sage ? `Sage: ${sage}` : ''].filter(Boolean).join('  ·  ');
};

const fieldCls = (disabled?: boolean, invalid?: boolean) =>
  `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
    invalid ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
  } ${disabled ? 'opacity-60' : ''}`;

const LockedField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {value === '' || value == null ? <span className="text-gray-400">—</span> : value}
    </div>
  </div>
);

const FscTariffEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  // Stateful so a create can advance the drawer onto the newly-inserted recno
  // (transitioning create -> edit) instead of closing.
  const [recno, setRecno] = useState(row ? Number(row.recno) : 0);

  const [mode, setMode] = useState<'create' | 'edit' | 'newrev' | 'dupe'>(isNew ? 'create' : 'edit');
  const [record, setRecord] = useState<TablemaintRow>({});
  const [form, setForm] = useState<TablemaintRow>(isNew ? { ...NEW_DEFAULTS } : {});
  const [formDeleted, setFormDeleted] = useState(false);
  const [options, setOptions] = useState<FscTariffFormOptions | null>(null);
  const [hasLines, setHasLines] = useState(false);
  const [linesExpired, setLinesExpired] = useState(false);
  const [linesOpen, setLinesOpen] = useState(false);
  const [preview, setPreview] = useState<TariffPreviewResponse | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Set right after a create / dupe keeps the drawer open in edit mode on the new
  // record; shows what to do next. Cleared once the user opens the lines sub-drawer.
  const [justCreated, setJustCreated] = useState<'create' | 'dupe' | null>(null);
  const topRef = useRef<HTMLDivElement>(null);
  // The edit-mode form as it stood when Dupe was pressed — Cancel in dupe mode
  // restores it (nothing was persisted) instead of discarding the user's edits.
  const preDupeRef = useRef<{ form: TablemaintRow; deleted: boolean } | null>(null);
  // Multi-bill-to junction (edit mode only): bill-tos this tariff applies to
  // BEYOND the primary fsc_tariff.billto. Keyed by tariffnumber (shared across
  // revisions), so it's loaded/saved independently of the base record.
  const [addlBillTos, setAddlBillTos] = useState<number[]>([]);
  const [addlNames, setAddlNames] = useState<Record<number, string>>({});
  // False until the junction fetch settles — the section shows "Loading…"
  // instead of a premature "No additional bill-tos." (two round-trips: the
  // record GET resolves tariffnumber first, then the junction loads).
  const [addlLoaded, setAddlLoaded] = useState(false);

  const seedForm = useCallback((r: TablemaintRow) => {
    setForm({
      tarifftype: s(r.tarifftype), fuelindex: s(r.fuelindex), ratetype: s(r.ratetype),
      otrlocal: s(r.otrlocal), rateeffective: s(r.rateeffective),
      billto: Number(r.billto) || 0, billtoname: s(r.cname), mas90code: s(r.mas90code),
      // The bill-to option list holds ACTIVE records only — carry the target's own
      // soft-delete so a tariff pointed at a deleted bill-to still says so.
      billtodeleted: s(r.billtodeleted),
      name: s(r.name),
      effectivedate: toInputDate(r.effectivedate), expirationdate: toInputDate(r.expirationdate),
      comments: s(r.comments), tariffnumber: s(r.tariffnumber), revisionnumber: s(r.revisionnumber),
    });
    setFormDeleted(s(r.deleted) === 'Y');
  }, []);

  const loadLines = useCallback((signal?: AbortSignal) => {
    fscTariffsService.getLines(recno, signal)
      .then((l) => { setHasLines(l.lines.length > 0); setLinesExpired(l.expired); })
      .catch(() => {});
  }, [recno]);

  const loadPreview = useCallback((signal?: AbortSignal) => {
    fscTariffsService.getLinesPreview(recno, signal).then(setPreview).catch(() => {});
  }, [recno]);

  // Form option lists load once.
  useEffect(() => {
    const ctrl = new AbortController();
    fscTariffsService.formOptions(ctrl.signal).then(setOptions).catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Load the record + lines + preview whenever a real recno is present: on open
  // in edit mode, and again after a create advances recno onto the new row.
  useEffect(() => {
    if (!recno) return;
    const ctrl = new AbortController();
    tablemaintService.get(TABLE, recno).then((r) => { setRecord(r); seedForm(r); }).catch(() => {});
    loadLines(ctrl.signal);
    loadPreview(ctrl.signal);
    return () => ctrl.abort();
  }, [recno, seedForm, loadLines, loadPreview]);

  // Load the tariff's additional bill-tos once its tariffnumber is known (the
  // junction keys on tariffnumber, resolved from the fetched record).
  useEffect(() => {
    const tn = Number(record.tariffnumber);
    if (!tn) return;
    const ctrl = new AbortController();
    fscTariffsService.getBillTos(tn, ctrl.signal)
      .then((list) => {
        setAddlBillTos(list.map((x) => x.billto));
        setAddlNames(Object.fromEntries(list.map((x) => [x.billto, x.name])));
        setAddlLoaded(true);
      })
      // A failed load still settles the section — better a wrong "none" than
      // an eternal "Loading…" (an aborted fetch simply re-runs on reopen).
      .catch(() => { if (!ctrl.signal.aborted) setAddlLoaded(true); });
    return () => ctrl.abort();
  }, [record.tariffnumber]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  // ── Preloaded typeahead option lists + meta maps (V1 billto/customer lookups) ──
  const billToOpts = useMemo(
    () => (options?.billTos ?? []).map((b) => ({
      value: String(b.recno), label: titleCase(b.name), labelSuffix: `#${b.recno}`,
      description: addrDesc(b.address, b.city, b.state, b.mas90code),
    })),
    [options],
  );
  const billToMeta = useMemo(() => {
    const m = new Map<string, { name: string; mas90code: string }>();
    (options?.billTos ?? []).forEach((b) => m.set(String(b.recno), { name: b.name, mas90code: b.mas90code }));
    return m;
  }, [options]);
  // ── Additional bill-tos (multi-bill-to junction) ──
  const billToLabel = useCallback(
    (recno: number): string => billToMeta.get(String(recno))?.name || addlNames[recno] || `#${recno}`,
    [billToMeta, addlNames],
  );
  // The add-picker excludes the primary bill-to and anything already added.
  const addBillToOpts = useMemo(
    () => billToOpts.filter((o) => Number(o.value) !== Number(form.billto) && !addlBillTos.includes(Number(o.value))),
    [billToOpts, form.billto, addlBillTos],
  );
  const addBillTo = (recno: number) => {
    if (recno > 0 && recno !== Number(form.billto) && !addlBillTos.includes(recno)) {
      setAddlBillTos((p) => [...p, recno]);
    }
  };
  const removeBillTo = (recno: number) => setAddlBillTos((p) => p.filter((b) => b !== recno));
  // Chips render alphabetically by name (recno tiebreak) regardless of add order.
  const sortedAddlBillTos = useMemo(
    () => [...addlBillTos].sort((a, b) => billToLabel(a).localeCompare(billToLabel(b)) || a - b),
    [addlBillTos, billToLabel],
  );

  const isIndexType = s(form.tarifftype) === 'FUEL INDEX';
  const wasDeleted = s(record.deleted) === 'Y';
  const isCurrentRevision = Number(record.revisionnumber) === Number(record.maxrevision);
  // Once a tariff has lines, the identity + rate-setup fields lock — dates &
  // comments stay editable. Dates additionally lock on a non-current revision.
  const baseLocked = mode === 'edit' && hasLines;
  const datesLocked = mode === 'edit' && !isCurrentRevision;
  // Type is locked on both clone modes: the copied lines are keyed by it (fuel
  // index brackets vs. effective dates), so switching it would orphan them.
  const typeLocked = mode === 'newrev' || mode === 'dupe' || baseLocked;
  const newRevisionEligible = mode === 'edit' && isCurrentRevision && !wasDeleted;
  // Dupe is a fresh tariff, so it's offered on ANY saved revision — superseded or
  // soft-deleted ones included (copying an old setup is exactly the use case).
  const dupeEligible = mode === 'edit' && recno > 0;

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!(Number(form.billto) > 0)) errs.push('Bill To is required.');
    if (!s(form.effectivedate).trim()) errs.push('Effective Date is required.');
    return errs;
  }, [form]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const invalidBillto = showErrors && !(Number(form.billto) > 0);
  const invalidEff = showErrors && !s(form.effectivedate).trim();

  const buildPayload = (): TablemaintRow => ({
    tarifftype: s(form.tarifftype),
    name: s(form.name),
    billto: Number(form.billto) || 0,
    effectivedate: toStoreDate(s(form.effectivedate)),
    expirationdate: toStoreDate(s(form.expirationdate)),
    fuelindex: s(form.fuelindex),
    rateeffective: s(form.rateeffective),
    otrlocal: s(form.otrlocal),
    ratetype: s(form.ratetype),
    comments: s(form.comments).toUpperCase(),
  });

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (mode === 'create') {
        const { recno: newRecno } = await tablemaintService.create(TABLE, payload);
        onSaved();                 // the row exists now — refresh the underlying list
        // Keep the drawer open on the new record so rate lines can be added
        // (they need the recno, which doesn't exist until the tariff is saved).
        setJustCreated('create');
        setMode('edit');
        setRecno(Number(newRecno));
        return;
      }
      if (mode === 'dupe') {
        const { recno: newRecno } = await fscTariffsService.dupe({ ...payload, sourceRecno: recno });
        onSaved();
        // Land the drawer on the copy (lines came with it) so its rates and extra
        // bill-tos can be tuned right away. The junction is NOT copied — drop the
        // source's chips so a follow-up save can't write them onto the new tariff.
        setAddlBillTos([]);
        setAddlNames({});
        setAddlLoaded(false);
        preDupeRef.current = null;
        setJustCreated('dupe');
        setMode('edit');
        setRecno(Number(newRecno));
        return;
      }
      if (mode === 'newrev') {
        await fscTariffsService.newRevision({ ...payload, sourceRecno: recno });
      } else {
        await tablemaintService.update(TABLE, recno, { ...payload, deleted: formDeleted ? 'Y' : 'N' });
        // Persist the additional bill-tos (junction keyed on tariffnumber). Runs
        // even when the base record is locked — bill-to reach isn't V1-locked.
        const tn = Number(record.tariffnumber);
        if (tn) await fscTariffsService.saveBillTos(tn, addlBillTos);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, mode, form, formDeleted, recno, record.tariffnumber, addlBillTos, onClose, onSaved]);

  // V1: New Revision first persists this revision (its expiration must be set),
  // then opens the form for the next revision with the dates cleared.
  const handleNewRevision = useCallback(async () => {
    if (!s(form.expirationdate).trim()) {
      setSaveError('Set an Expiration Date on this revision before creating a new revision.');
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      await tablemaintService.update(TABLE, recno, { ...buildPayload(), deleted: formDeleted ? 'Y' : 'N' });
      onSaved(); // the prior revision is now persisted — refresh the underlying list
      setMode('newrev');
      setForm((p) => ({ ...p, effectivedate: '', expirationdate: '' }));
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save this revision.');
    } finally {
      setIsSaving(false);
    }
  }, [form, formDeleted, recno, onSaved]);

  // Dupe stages a copy in the form (nothing is written until Save) so the bill-to,
  // dates and rate setup can be changed BEFORE the copy exists — once it does, its
  // copied lines re-lock those fields.
  const startDupe = useCallback(() => {
    preDupeRef.current = { form, deleted: formDeleted };
    setSaveError(null);
    setShowErrors(false);
    setJustCreated(null);
    setFormDeleted(false);        // a copy is never born soft-deleted
    setMode('dupe');
  }, [form, formDeleted]);

  const cancelDupe = useCallback(() => {
    const prev = preDupeRef.current;
    if (prev) { setForm(prev.form); setFormDeleted(prev.deleted); }
    preDupeRef.current = null;
    setSaveError(null);
    setShowErrors(false);
    setMode('edit');
  }, []);

  const sel = (key: string, label: string, list: string[], disabled?: boolean, clearable = false) => (
    <SelectField label={label} disabled={isSaving || disabled} clearable={clearable}
      value={s(form[key])} onChange={(v) => setField(key, v)} options={opts(list)} />
  );

  const saveLabel = mode === 'create' ? 'Create Tariff'
    : mode === 'newrev' ? 'Create Revision'
      : mode === 'dupe' ? 'Create Duplicate' : 'Save Changes';
  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {newRevisionEligible && (
          <button type="button" onClick={handleNewRevision} disabled={isSaving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50">
            <DocumentDuplicateIcon className="w-4 h-4" /> New Revision
          </button>
        )}
        {dupeEligible && (
          <button type="button" onClick={startDupe} disabled={isSaving}
            title="Copy this tariff (and its rate lines) into a brand-new tariff number"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50">
            <Square2StackIcon className="w-4 h-4" /> Dupe
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={mode === 'dupe' ? cancelDupe : onClose} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
          Cancel
        </button>
        <button onClick={handleSave} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
            : (<><CheckIcon className="w-4 h-4" /> {saveLabel}</>)}
        </button>
      </div>
    </div>
  );

  const title =
    mode === 'create' ? 'Add Fuel Surcharge Tariff'
      : mode === 'newrev' ? `New Revision — Tariff #${s(form.tariffnumber)}`
        : mode === 'dupe' ? `Dupe of Tariff #${s(record.tariffnumber)} · Rev ${s(record.revisionnumber)}`
          : `Edit Tariff #${s(record.tariffnumber)} · Rev ${s(record.revisionnumber)}`;

  const previewRows = preview?.rows ?? [];
  const indexLabel = preview?.indexSource ?? '';

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="56rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {mode === 'edit' && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} label={`Tariff #${s(record.tariffnumber)}`} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}
        {justCreated && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/30 p-3 text-sm text-emerald-800 dark:text-emerald-300">
            {justCreated === 'dupe' ? (
              <>Tariff #{s(record.tariffnumber) || s(form.tariffnumber)} created as a copy, with the source's rate lines. Review them under <b>Add / Edit Tariff Lines</b>, add any additional bill-tos (those aren't copied), then close when you're done.</>
            ) : (
              <>Tariff #{s(record.tariffnumber) || s(form.tariffnumber)} created. Add its rate lines with <b>Add / Edit Tariff Lines</b> below, then close when you're done.</>
            )}
          </div>
        )}
        {mode === 'dupe' && (
          <div className="rounded-lg border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/30 p-3 text-sm text-blue-800 dark:text-blue-300">
            Copying tariff #{s(record.tariffnumber)} rev {s(record.revisionnumber)} into a <b>brand-new tariff</b> (revision 1).
            Its rate lines come along; the Type stays fixed and additional bill-tos aren't copied.
            Change the Bill To, dates and rate setup now — they lock again once the copy has lines.
            The original is left untouched.
          </div>
        )}
        {baseLocked && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-3 text-sm text-amber-800 dark:text-amber-300">
            {isCurrentRevision ? (
              <>This tariff has lines — its bill-to and rate setup are locked. You can still edit the dates, comments and tariff lines below{newRevisionEligible ? <>, or use <b>New Revision</b> to supersede it</> : null}.</>
            ) : (
              <>This is a superseded revision (rev {s(record.revisionnumber)} of {s(record.maxrevision)}) — its base information and dates are locked. You can still edit the comments{linesExpired ? '' : ' and tariff lines'} below.</>
            )}
          </div>
        )}

        {/* Identity */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Tariff</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sel('tarifftype', 'Type', TARIFF_TYPES, typeLocked)}
            {(mode !== 'create') && (
              <div className="grid grid-cols-2 gap-3">
                {/* A dupe gets its own number (recno-derived), assigned on save. */}
                <LockedField label="Tariff #" value={mode === 'dupe' ? 'NEW' : s(form.tariffnumber)} />
                <LockedField label="Revision" value={mode === 'newrev' ? 'NEW' : mode === 'dupe' ? '1' : s(form.revisionnumber)} />
              </div>
            )}
            <SelectField label="Bill To" required invalid={invalidBillto} disabled={isSaving || baseLocked}
              clearable={false} showDescriptions value={Number(form.billto) > 0 ? String(form.billto) : ''}
              options={billToOpts}
              {...deletedRefProps('Bill To', form.billtoname, form.billto, form.billtodeleted)}
              onChange={(v) => {
                const meta = billToMeta.get(v);
                setForm((p) => ({ ...p, billto: Number(v) || 0, billtoname: meta?.name || '', mas90code: meta?.mas90code || '' }));
              }} />
            <LockedField label="Sage Tag" value={s(form.mas90code)} />
            {/* Name/Description — free-text label for the tariff (optional, stored
                as-typed). Replaces the old per-customer link; stays editable even
                when the tariff is locked, since it has no rate impact. */}
            <div className="md:col-span-2">
              <label htmlFor="fsc-tariff-name" className="block text-sm font-medium text-label mb-1">
                Name/Description
              </label>
              <input id="fsc-tariff-name" type="text" maxLength={120} value={s(form.name)}
                disabled={isSaving} placeholder="Optional label for this tariff"
                onChange={(e) => setField('name', e.target.value)} className={fieldCls(isSaving)} />
            </div>
          </div>
        </fieldset>

        {/* Additional bill-tos (multi-bill-to; edit mode only — the junction keys
            on tariffnumber, which doesn't exist until the tariff is created).
            Stays editable even when the base record is locked — a tariff's bill-to
            reach is FastCMS metadata, not V1-locked identity. */}
        {mode === 'edit' && (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-label">Additional Bill-Tos</legend>
            <p className="text-xs text-muted mb-3">
              These bill-tos also see this tariff on gwi360 quotes, in addition to the primary Bill To above (which V1 uses).
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {!addlLoaded ? (
                <span className="text-sm text-gray-400">Loading…</span>
              ) : addlBillTos.length === 0 ? (
                <span className="text-sm text-gray-400">No additional bill-tos.</span>
              ) : (
                sortedAddlBillTos.map((b) => (
                  <span key={b}
                    className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-sm rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                    {titleCase(billToLabel(b))}
                    <span className="text-xs text-blue-500/70 dark:text-blue-300/60">#{b}</span>
                    <button type="button" onClick={() => removeBillTo(b)} disabled={isSaving}
                      aria-label={`Remove ${billToLabel(b)}`}
                      className="rounded-full p-0.5 hover:bg-blue-200/60 dark:hover:bg-blue-800/60 disabled:opacity-50">
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="max-w-md">
              <SelectField key={`add-billto-${addlBillTos.length}`} label="Add a bill-to"
                value="" showDescriptions clearable={false} disabled={isSaving}
                options={addBillToOpts} onChange={(v) => addBillTo(Number(v))} />
            </div>
          </fieldset>
        )}

        {/* Rate setup */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Rate Setup</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DateField label="Effective Date" required value={s(form.effectivedate)} invalid={invalidEff}
              disabled={isSaving || datesLocked} onChange={(v) => setField('effectivedate', v)} />
            <DateField label="Expiration Date" value={s(form.expirationdate)}
              disabled={isSaving || datesLocked} onChange={(v) => setField('expirationdate', v)} />
            {sel('ratetype', 'Rate Type', RATE_TYPES, baseLocked)}
            {sel('otrlocal', 'OTR / Local', OTR_LOCAL, baseLocked)}
            {/* Fuel Index source + Rate Effective apply only to FUEL INDEX tariffs. */}
            {isIndexType && sel('fuelindex', 'Fuel Index', FUEL_INDEX_SRC, baseLocked)}
            {isIndexType && sel('rateeffective', 'Rate Effective', RATE_EFFECTIVE, baseLocked)}
          </div>
        </fieldset>

        {/* Comments (always editable) */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Comments</legend>
          <textarea rows={3} value={s(form.comments)} disabled={isSaving}
            onChange={(e) => setField('comments', e.target.value)} className={fieldCls(isSaving)} />
        </fieldset>

        {/* Read-only rate schedule around the current index/date (V1 inline preview) */}
        {mode === 'edit' && previewRows.length > 0 && (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-label">Rate Schedule</legend>
            {preview?.currentIndex && (
              <p className="text-xs text-muted mb-2">
                Current {indexLabel} index: <b>{preview.currentIndex}</b>
              </p>
            )}
            <table className="text-sm w-full max-w-sm">
              <thead>
                <tr className="text-left text-muted border-b border-gray-200 dark:border-gray-700">
                  <th className="py-1 font-medium">{preview?.tariffType === 'FUEL INDEX' ? 'Fuel Index' : 'Effective Date'}</th>
                  <th className="py-1 font-medium text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <td className="py-1 text-gray-800 dark:text-gray-200">
                      {preview?.tariffType === 'FUEL INDEX' ? `${r.key}${r.keyEnd ? ` - ${r.keyEnd}` : ''}` : fmtMDY(r.key)}
                    </td>
                    <td className="py-1 text-right text-gray-800 dark:text-gray-200">
                      {preview?.rateType === 'PER MILE' || preview?.rateType === 'FLAT' ? '$' : ''}{r.rate}{preview?.rateType === 'STANDARD' ? '%' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </fieldset>
        )}

        {/* Tariff lines (edit mode only; New Revision lives in the footer) */}
        {mode === 'edit' && !linesExpired && (
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => { setJustCreated(null); setLinesOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
              <PencilSquareIcon className="w-4 h-4" /> Add / Edit Tariff Lines
            </button>
          </div>
        )}

        {/* Deleted */}
        {mode === 'edit' && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={formDeleted} disabled={isSaving}
                onChange={(e) => setFormDeleted(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="text-sm">
                <span className="font-medium text-label">Deleted</span>
                <span className="block text-xs text-muted">
                  Soft-deletes the tariff — hidden from filters but preserved in history. Uncheck and save to restore.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      {linesOpen && (
        <FscTariffLinesDrawer tariffRecno={recno} onClose={() => setLinesOpen(false)}
          onSaved={() => { loadLines(); loadPreview(); }} />
      )}
    </Drawer>
  );
};

export default FscTariffEditDrawer;
